import { CourseBusyNotice } from './CourseBusyNotice'
import { EditBlockSettings } from './EditBlockSettings'
import { MascotEmptyState } from './EmptyStates'
import { GenerationSettings } from './GenerationSettings'
import { JobLostNotice } from './JobLostNotice'
import { MessageList } from './messages/MessageList'
import { PreviousRunNotice } from './PreviousRunNotice'
import { RunOutcome } from './RunOutcome'
import type { useAiChat } from './useAiChat'
import type { useEditScope } from './useEditScope'
import type { useGenerationSettings } from './useGenerationSettings'
import type { useSidebarBusyFlow } from './useSidebarBusyFlow'
import type { BlockEditQuoteView } from '@/features/ai/useBlockEditQuote'
import type { CourseQuoteView } from '@/features/ai/useCourseQuote'
import type { SidebarMode } from './types'

interface Props {
  mode: SidebarMode
  chat: ReturnType<typeof useAiChat>
  busy: ReturnType<typeof useSidebarBusyFlow>
  settings: ReturnType<typeof useGenerationSettings>
  /** Цена прогона и баланс — рисуются рядом с «Кол-во уроков» (I-2). */
  quote: CourseQuoteView
  /** Область правки (этот блок / весь урок) — единственный источник, тот же,
   *  которым `AISidebar.submit` кормит `sendBlockEdit`. */
  editScope: ReturnType<typeof useEditScope>
  editQuote: BlockEditQuoteView
  /** E8: в области правки есть блок с несохранённым черновиком — старт
   *  гашен в `composerGates.ts`, здесь только объяснение причины. */
  hasUnsavedBlockInScope: boolean
}

/**
 * Какой экран показать — вынесено из `SidebarScreen`, чтобы держать сложность
 * рендера низкой (eslint `complexity`): все составные условия (§ занятость,
 * настройки, лента) живут в ОДНОЙ функции, рендер ниже — плоская цепочка
 * простых `if` по её результату.
 *
 * Занятость курса гасит ОБА режима одинаково (`showBusyNotice`):
 * разъехавшиеся гейты между режимами — ровно тот класс дефекта, что ловили
 * в под-проектах C и D. Экран настроек области правки НЕ требует выделенного
 * блока (Important 1, ревью Task 3): `selectLesson` в `editorSlice` явно
 * обнуляет `selectedBlockId` — «урок выбран, блок нет» штатное состояние
 * после клика по уроку в таймлайне, и в нём область «Весь урок» обязана быть
 * достижима. `EditBlockSettings` (Task 2) уже умеет объяснять текстом ВСЕ
 * пустые случаи («выделите блок» / «в уроке нет блоков» / «загружаю блоки»)
 * — отдельная карточка-заглушка `SelectBlockHint` поверх нужного экрана была
 * бы фальшивым дублем, поэтому она отсюда убрана целиком, а не оставлена
 * недостижимой веткой.
 */
const resolveScreenFlags = (
  mode: SidebarMode,
  chat: Pick<ReturnType<typeof useAiChat>, 'isRunning' | 'items'>,
  busy: Pick<ReturnType<typeof useSidebarBusyFlow>, 'isBusy'>,
) => {
  const isEditOrGenerate = mode === 'generate' || mode === 'edit-block'
  return {
    showSettings: mode === 'generate' && !chat.isRunning && !busy.isBusy,
    showEditSettings: mode === 'edit-block' && !chat.isRunning && !busy.isBusy,
    showBusyNotice: isEditOrGenerate && !chat.isRunning && busy.isBusy,
    // `isRunning` обязателен: реплика автора появляется в ленте только после
    // ответа POST.
    showMessages: chat.items.length > 0 || chat.isRunning,
  }
}

const SidebarScreen = ({
  mode,
  chat,
  busy,
  settings,
  quote,
  editScope,
  editQuote,
  hasUnsavedBlockInScope,
}: Props) => {
  const flags = resolveScreenFlags(mode, chat, busy)

  // F8 (§7.4): итог перевода — единственный раз ПРОИЗВОДНОЕ реестра, а не
  // самой опрашиваемой джобы: после цепочки `job` — уже картинки. Общая для
  // веток итога и «прогон идёт» — чтобы не держать JSX ленты в двух местах.
  const feed = (
    <>
      {chat.entry?.previous ? <PreviousRunNotice previous={chat.entry.previous} /> : null}
      <MessageList
        items={chat.items}
        isRunning={chat.isRunning}
        isAwaitingAuthor={chat.isAwaitingAuthor}
        isStatusUnknown={chat.isStatusUnknown}
        progress={chat.progress}
        jobType={chat.jobType}
        onAnswerGate={chat.answerGate}
        isAnsweringGate={chat.isAnsweringGate}
        gateFailure={chat.gateFailure}
      />
    </>
  )

  // Перевод идёт: занятость объяснять незачем, его прогресс — в меню шапки.
  if (chat.hidesTranslation && chat.isRunning) return <MascotEmptyState />

  if (chat.outcome && !chat.hidesTranslation) {
    // Итог рисуется НАД лентой, а не вместо неё: за ранним возвратом прятались
    // и вопрос, на который автор не ответил, и события того, что он собирается
    // доделать. Крестик по-прежнему `detach` — «этот прогон мне больше не
    // нужен», иначе запись реестра осталась бы в persist навсегда (см.
    // «Владение очисткой реестра» в useCourseJob).
    return (
      <>
        <RunOutcome
          job={chat.outcome}
          progress={chat.progress}
          onDismiss={chat.dismissOutcome}
          onResume={chat.resumeRun}
          onShowResumed={chat.showResumedRun}
          isResuming={chat.isResuming}
          resumeFailure={chat.resumeFailure}
          isFrozen={chat.isFrozen}
        />
        {feed}
      </>
    )
  }
  // Выше настроек и плашки занятости по той же причине, что и итог: объяснение
  // пропавшей ленты обязано быть достижимым, иначе отметку никто не снимет.
  if (chat.isLost) return <JobLostNotice onDismiss={chat.dismissLost} />
  if (flags.showSettings) return <GenerationSettings settings={settings} quote={quote} />
  if (flags.showEditSettings) {
    return <EditBlockSettings scope={editScope} quote={editQuote} />
  }
  if (flags.showBusyNotice && busy.activeJob) {
    const job = busy.activeJob
    return (
      <CourseBusyNotice
        jobId={job.job_id}
        jobType={job.job_type}
        status={job.status}
        onShow={() => busy.handleShowRun(job.job_id, job.job_type)}
      />
    )
  }
  if (flags.showMessages && !chat.hidesTranslation) return feed
  return <MascotEmptyState />
}

/**
 * Единственное место, решающее, какой экран показать в теле панели —
 * `mode`/`chat`/`busy` уже собраны у вызывающего, поэтому «какой экран
 * показать» не размазано по отдельному хуку с производными флагами.
 *
 * Итог своего прогона — выше настроек, плашки занятости И ленты (I-2): он
 * обязан быть достижим ВСЕГДА, иначе `dismissOutcome` (= `detach`)
 * недостижим следом, и запись остаётся в persist навсегда. Над лентой, а не
 * вместо неё — см. ветку `if (chat.outcome)` в `SidebarScreen` ниже.
 *
 * Высоту тела держит ЭТА обёртка, одна на все ветки (B1): экраны-карточки
 * (итог, «курс занят», «прогон пропал») её не растягивали, и композер вставал
 * посреди панели — с `flex-1` по отдельным экранам следующая карточная ветка
 * уехала бы туда же. Внутри обёртки экраны прижаты к верху; `flex-1` внутри
 * ленты и настроек — уже про другое: там содержимое ЗАПОЛНЯЕТ отданную высоту
 * (скролл, вертикальное центрирование маскота).
 */
export const SidebarBody = (props: Props) => (
  <div data-testid="ai-sidebar-body" className="flex min-h-0 flex-1 flex-col">
    <SidebarScreen {...props} />
  </div>
)
